import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { DatePicker } from '@components/datePicker';
import { FormItemContainer } from '@components/form/FormItemContainer';
import { FormVerticalContainer } from '@components/form/FormVerticalContainer';
import { Button } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';

type Props = {
  selectedMonth: Dayjs | null;
  hasUsage: boolean;
  onChange: (date: Dayjs | null) => void;
  onChangeIsManualBilling: (isManualBilling: boolean) => void;
  onNextStep: (month: Dayjs | null) => void;
  onClose: () => void;
};
export const BillingMonthSelect = ({
  selectedMonth,
  hasUsage,
  onChange,
  onChangeIsManualBilling,
  onNextStep,
  onClose,
}: Props) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isManualBilling, setIsManualBilling] = useState<boolean>(false);

  const handleChangeIsManualBilling = () => {
    const newState = !isManualBilling;
    setIsManualBilling(newState);
    onChangeIsManualBilling(newState);
  };

  const handleNextStep = () => {
    onNextStep(selectedMonth);
  };

  return (
    <>
      <Content>
        <DescriptionWrapper>
          <Description>{t('Acc_Create_06_01')}</Description>
          <Description>{t('Acc_Create_06_02')}</Description>
        </DescriptionWrapper>
        <FormVerticalContainer style={{ height: '100%' }}>
          <FormItemContainer>
            <RequiredFieldKey htmlFor="billing-date">{t('Acc_Main_06')}</RequiredFieldKey>
            <DatePicker
              inputFormat={t('DateFormat_YM')}
              value={selectedMonth}
              onChange={onChange}
              views={['year', 'month']}
              openTo="month"
              renderInput={params => <DatePicker.Input {...params} id="billing-date" fullWidth />}
            />
          </FormItemContainer>
          <FormItemContainer style={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
            {!hasUsage && (
              <>
                <ErrorIconWrapper>
                  <ErrorIcon width={22} height={22} color={colors['ic-primary-light']} />
                </ErrorIconWrapper>
                <EmptyDescription>{t('Billing.addBilling.noUsageData')}</EmptyDescription>
              </>
            )}
          </FormItemContainer>
          <FormItemContainer>
            <CheckboxPosition>
              <input type="checkbox" checked={isManualBilling} onChange={handleChangeIsManualBilling} />
              <TextBox>{t('Billing.addBilling.addManualBilling')}</TextBox>
            </CheckboxPosition>
            <InfoBox>
              <TextLi>{t('Billing.addBilling.manualBillingDesc')}</TextLi>
            </InfoBox>
          </FormItemContainer>
        </FormVerticalContainer>
      </Content>
      <Footer>
        <Button
          variant="contain"
          size="extraLarge"
          paddingHorizontal={99.5}
          disabled={!selectedMonth}
          onClick={handleNextStep}
        >
          {t('Acc_Create_37')}
        </Button>
        <Button variant="outline" size="extraLarge" paddingHorizontal={98.5} onClick={onClose}>
          {t('GNB_Profile_MyProfile_11')}
        </Button>
      </Footer>
    </>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 24px 28px 16px 28px;
`;

const Footer = styled.div`
  display: flex;
  padding: 24px 0px 28px;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const DescriptionWrapper = styled.div`
  margin-bottom: 28px;
`;

const Description = styled.p`
  ${fonts.Body2}
  white-space: pre-wrap;

  & + & {
    margin-top: 2px;
  }
`;

const RequiredFieldKey = styled.label`
  display: inline-block;
  ${fonts.Headline8}
  margin-bottom: 6px;
  &:after {
    content: '*';
    color: red;
    margin-left: 2px;
  }
  &.disabled {
    color: ${({ theme }) => theme.colors['text-gray-light']};
    &:after {
      color: ${({ theme }) => theme.colors['text-red-lighter']};
    }
  }
`;

const CheckboxPosition = styled.div`
  display: flex;
`;

const InfoBox = styled.div`
  padding: 12px 33px 12px 28px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light-50']};
`;

const TextBox = styled.div`
  ${fonts.Body2};
  list-style: none;
  padding: 12px 33px 12px 16px;
  display: flex;
`;

const TextLi = styled.li`
  position: relative;
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  list-style-type: none;
  white-space: pre;
  &::before {
    position: absolute;
    content: '•';
    color: ${({ theme }) => theme.colors['ic-gray-light']};
    font-size: 1.5em;
    left: -15px;
    top: -2px;
    opacity: 30%;
  }
`;
const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: ${({ theme: { colors } }) => colors['bg-primary-lighter']};
`;
const EmptyDescription = styled.p`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
`;
